'use client'

import React from 'react'
import {
  CheckCircle2,
  FileText,
  Shield,
  Bot,
  BarChart3,
  BookOpen,
  Code,
  Layers,
  TrendingUp,
  ArrowRight,
  Zap,
} from 'lucide-react'

/**
 * AI Search Results Diagrams
 * Visual components for Part 4 of The AI Search Playbook
 * Brand colors: Primary #008dcc, Accent #FDC304, Pink #F90B8A, Purple #8b5cf6
 */

/**
 * GEO Implementation Audit - Before vs After
 */
export function GEOImplementationAudit() {
  const items = [
    {
      category: 'Crawl Access (robots.txt)',
      before: 'Basic robots.txt, no AI-specific rules',
      after:
        '6 AI bots explicitly allowed, training bots blocked, key pages specified',
      status: 'done',
    },
    {
      category: 'llms.txt',
      before: 'Did not exist',
      after:
        '262-line Markdown file with products, services, portfolio, URLs, use policy',
      status: 'done',
    },
    {
      category: 'AI Capabilities Factsheet',
      before: 'Did not exist',
      after:
        'Explicit corrections for common AI misinterpretations of our services',
      status: 'done',
    },
    {
      category: 'FAQ Schema (JSON-LD)',
      before: '0 posts with FAQPage schema',
      after: '37 of 39 posts with BlogFAQ components and FAQPage JSON-LD',
      status: 'done',
    },
    {
      category: 'Article Schema',
      before: 'Basic metadata only',
      after:
        'Full Article JSON-LD on every post (author, publisher, dates, images)',
      status: 'done',
    },
    {
      category: 'AI Referrer Tracking',
      before: 'No AI traffic tracking',
      after:
        'Custom tracker detecting ChatGPT, Perplexity, Claude, Gemini, Copilot referrals',
      status: 'done',
    },
    {
      category: 'Content Structure',
      before: 'Mixed quality, no consistent format',
      after:
        'Answer-first, TLDR boxes, comparison tables, 3,000-5,000 words per post',
      status: 'done',
    },
    {
      category: 'Series-Based Content',
      before: 'Standalone posts',
      after: '4 multi-part series (4+4+3+2 = 13 interlinked posts)',
      status: 'done',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <CheckCircle2 className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            GEO Implementation Audit: Before vs After
          </span>
        </div>
        <div className='space-y-3'>
          {items.map(item => (
            <div
              key={item.category}
              className='rounded-lg border border-border/60 p-3'
            >
              <div className='mb-2 flex items-center gap-2'>
                <CheckCircle2 className='h-4 w-4 shrink-0 text-green-500' />
                <span className='text-sm font-semibold text-foreground'>
                  {item.category}
                </span>
              </div>
              <div className='grid grid-cols-2 gap-3 pl-6'>
                <div>
                  <span className='text-xs font-medium text-red-500'>
                    Before
                  </span>
                  <p className='text-xs text-muted-foreground'>{item.before}</p>
                </div>
                <div>
                  <span className='text-xs font-medium text-green-500'>
                    After
                  </span>
                  <p className='text-xs text-foreground/80'>{item.after}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Content Cluster Strategy - Visual of series-based approach
 */
export function ContentClusterStrategy() {
  const clusters = [
    {
      series: 'AI Search Playbook',
      parts: 4,
      topics: [
        'Traffic shift data',
        'SEO/GEO/AEO framework',
        'GEO tactics',
        'Our results',
      ],
      color: '#008dcc',
      icon: TrendingUp,
    },
    {
      series: 'AI Technical Debt',
      parts: 4,
      topics: [
        'Vibe coding crisis',
        'Thread-based engineering',
        'Claude Code guide',
        'Case study',
      ],
      color: '#8b5cf6',
      icon: Code,
    },
    {
      series: 'AI Native Agency',
      parts: 3,
      topics: ['Agency comparison', 'Why B2B brands move', 'Budget guide'],
      color: '#FDC304',
      icon: Layers,
    },
    {
      series: 'Growth Marketing',
      parts: 2,
      topics: ['Traditional vs growth', 'AI growth definitive guide'],
      color: '#F90B8A',
      icon: BarChart3,
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <BookOpen className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            Content Cluster Strategy: 13 Posts Across 4 Series
          </span>
        </div>
        <div className='space-y-4'>
          {clusters.map(c => {
            const Icon = c.icon
            return (
              <div
                key={c.series}
                className='rounded-lg border border-border/60 p-4'
              >
                <div className='mb-2 flex items-center justify-between'>
                  <div className='flex items-center gap-2'>
                    <Icon className='h-4 w-4' style={{ color: c.color }} />
                    <span className='font-semibold text-foreground'>
                      {c.series}
                    </span>
                  </div>
                  <span
                    className='rounded-full px-2 py-0.5 text-xs font-bold text-white'
                    style={{ backgroundColor: c.color }}
                  >
                    {c.parts} parts
                  </span>
                </div>
                <div className='flex flex-wrap gap-1.5 pl-6'>
                  {c.topics.map((topic, i) => (
                    <div key={topic} className='flex items-center gap-1'>
                      <span className='text-xs text-muted-foreground'>
                        {i + 1}. {topic}
                      </span>
                      {i < c.topics.length - 1 && (
                        <ArrowRight className='h-3 w-3 text-muted-foreground' />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
        <div className='mt-4 rounded-lg border border-[#008dcc]/20 bg-[#008dcc]/5 p-3'>
          <p className='text-xs text-foreground/80'>
            <strong>Why series work for GEO:</strong> AI platforms treat
            interlinked series as topical authority clusters. Each post
            reinforces the others, creating compound citation potential.
          </p>
        </div>
      </div>
    </div>
  )
}

/**
 * Technical GEO Stack - Architecture of AI discovery infrastructure
 */
export function TechnicalGEOStack() {
  const layers = [
    {
      name: 'Crawl Layer',
      icon: Shield,
      color: '#008dcc',
      items: [
        'robots.txt: 6 AI bots allowed, training bots blocked',
        'XML sitemap with accurate lastmod dates',
        'Key pages explicitly surfaced (/blogs, /services, /projects)',
      ],
    },
    {
      name: 'Discovery Layer',
      icon: FileText,
      color: '#8b5cf6',
      items: [
        'llms.txt: 262 lines covering products, services, portfolio',
        'ai-capabilities-factsheet.txt: Corrects AI misinterpretations',
        'Structured URLs with descriptive slugs',
      ],
    },
    {
      name: 'Schema Layer',
      icon: Code,
      color: '#FDC304',
      items: [
        'FAQPage JSON-LD on 37/39 blog posts',
        'Article JSON-LD with author, publisher, dates',
        'Organization schema on service pages',
      ],
    },
    {
      name: 'Measurement Layer',
      icon: BarChart3,
      color: '#F90B8A',
      items: [
        'Custom AI referrer tracker (ChatGPT, Perplexity, Claude, Gemini)',
        'UTM parameter detection for chatgpt.com sources',
        'Session-level AI attribution in analytics',
      ],
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <Bot className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            Our GEO Technical Stack
          </span>
        </div>
        <div className='space-y-4'>
          {layers.map((layer, idx) => {
            const Icon = layer.icon
            return (
              <div key={layer.name} className='relative'>
                <div className='flex gap-4'>
                  <div className='flex flex-col items-center'>
                    <div
                      className='flex h-8 w-8 items-center justify-center rounded-full text-white'
                      style={{ backgroundColor: layer.color }}
                    >
                      <Icon className='h-4 w-4' />
                    </div>
                    {idx < layers.length - 1 && (
                      <div className='mt-1 h-full w-0.5 bg-[var(--border)]' />
                    )}
                  </div>
                  <div className='flex-1 pb-4'>
                    <h5 className='font-semibold text-foreground'>
                      {layer.name}
                    </h5>
                    <ul className='mt-1.5 space-y-1'>
                      {layer.items.map(item => (
                        <li key={item} className='flex items-start gap-1.5'>
                          <Zap
                            className='mt-0.5 h-3 w-3 shrink-0'
                            style={{ color: layer.color }}
                          />
                          <span className='text-xs text-muted-foreground'>
                            {item}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

/**
 * Lessons Learned - Key takeaways
 */
export function GEOLessonsLearned() {
  const lessons = [
    {
      number: 1,
      title: 'Series beat standalone posts',
      detail:
        'AI platforms treat interlinked content clusters as topical authority. 4-part series generate more citations than 4 disconnected posts.',
      color: '#008dcc',
    },
    {
      number: 2,
      title: 'FAQ schema is optional markup',
      detail:
        '15 minutes per post to add FAQPage JSON-LD. We added it to 37 posts.',
      color: '#8b5cf6',
    },
    {
      number: 3,
      title: 'llms.txt is cheap insurance',
      detail:
        'Took 2 hours to create. Zero confirmed AI platforms read it yet. But 844,000 sites adopted it, and the downside is zero.',
      color: '#FDC304',
    },
    {
      number: 4,
      title: 'Block training, allow search',
      detail:
        'Our robots.txt blocks training bots (CCBot, Google-Extended) but allows search bots (GPTBot, PerplexityBot). We keep our content ours while staying discoverable.',
      color: '#F90B8A',
    },
    {
      number: 5,
      title: 'Track AI traffic separately from day one',
      detail:
        'Our custom AI referrer tracker differentiates ChatGPT, Perplexity, Claude, and Gemini traffic. Without this, AI visits disappear into "Direct" or "Referral."',
      color: '#008dcc',
    },
    {
      number: 6,
      title: 'Answer-first writing is a discipline',
      detail:
        'Putting the conclusion in paragraph 1 feels unnatural. But data-backed content with statistics in the opening gets cited 67% more by AI. We restructured every post.',
      color: '#8b5cf6',
    },
  ]

  return (
    <div data-blog-layout='narrow'>
      <div className='my-8 p-6 md:p-8 bg-muted/50 rounded-2xl border border-border/60'>
        <div className='mb-6 flex items-center gap-2'>
          <BookOpen className='h-5 w-5 text-[#008dcc]' />
          <span className='text-lg font-semibold text-foreground'>
            6 Lessons From Our GEO Implementation
          </span>
        </div>
        <div className='space-y-4'>
          {lessons.map(lesson => (
            <div key={lesson.number} className='flex gap-3'>
              <div
                className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white'
                style={{ backgroundColor: lesson.color }}
              >
                {lesson.number}
              </div>
              <div>
                <h5 className='text-sm font-semibold text-foreground'>
                  {lesson.title}
                </h5>
                <p className='mt-0.5 text-xs text-muted-foreground'>
                  {lesson.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
